import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth0User } from "../context/Auth0UserContext";
import { useUser } from "../context/UserContext";

// Simple switch between Auth0 and original authentication
const useAuth0 = process.env.REACT_APP_USE_AUTH0 === 'true';

// Test login backdoor for E2E testing (alpha only)
const testLoginEnabled = process.env.REACT_APP_ENABLE_TEST_LOGIN === 'true';

// Check for test user in localStorage (mirrors AdminProtectedRoute pattern)
function getTestUser(): any | null {
  if (!testLoginEnabled) return null;
  if (sessionStorage.getItem('testLoginActive') !== 'true') return null;
  try {
    const stored = localStorage.getItem('currentUser');
    if (stored) {
      const user = JSON.parse(stored);
      if (user && user.id) {
        console.log('🔐 [TEST MODE] Using test user from localStorage:', user.username);
        return user;
      }
    }
  } catch (e) {
    console.error('Failed to parse test user:', e);
  }
  return null;
}

// Auth0 Protected Route
function Auth0ProtectedRoute() {
  const { user: auth0User, loading } = useAuth0User();

  // In test mode, check for test user first
  const testUser = getTestUser();
  if (testUser) {
    return <ProtectedRouteComponent user={testUser} loading={false} />;
  }

  return <ProtectedRouteComponent user={auth0User} loading={loading} />;
}

// Original Protected Route
function OriginalProtectedRoute() {
  const { user } = useUser();
  return <ProtectedRouteComponent user={user} loading={false} />;
}

// Main Protected Route - Renders appropriate authentication wrapper
const ProtectedRoute: React.FC = () => {
  return useAuth0 ? <Auth0ProtectedRoute /> : <OriginalProtectedRoute />;
};

// Shared Protected Route Component
function ProtectedRouteComponent({ user, loading }: { user: any, loading: boolean }) {

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#2D2070' }} />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: window.location.pathname }} replace />;
  }

  return <Outlet />;
};

export default ProtectedRoute;